/*
 * PinLoginGate — 4-digit keypad login for the /staff till.
 *
 * UX: enter email + 4 digits → auto-submits on the 4th digit.
 * Errors are shown inline; a 429 lockout surfaces the "try again in X min"
 * message from the backend. `onPasswordLogin` opens the global AuthModal for
 * fresh accounts that haven't set a PIN yet.
 */
import React, { useState, useEffect } from "react";
import { ChefHat, Loader2, KeyRound, Delete } from "lucide-react";

const LS_LAST_EMAIL = "mm_staff_last_email";

export default function PinLoginGate({ onPasswordLogin, onPinSuccess, pinLogin, toast }) {
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem(LS_LAST_EMAIL) || ""; } catch { return ""; }
  });
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setError("");
    if (!email.trim()) { setError("Enter your email first."); return; }
    if (pin.length !== 4) { setError("Enter your 4-digit PIN."); return; }
    setSubmitting(true);
    try {
      await pinLogin(email.trim().toLowerCase(), pin);
      try { localStorage.setItem(LS_LAST_EMAIL, email.trim().toLowerCase()); } catch { /* ignore */ }
      onPinSuccess?.();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e.message;
      if (status === 429) {
        setError(detail || "Too many attempts. Try again shortly.");
      } else {
        setError(detail || "Invalid PIN.");
        setPin("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const tap = (d) => {
    if (submitting) return;
    setError("");
    setPin((p) => (p + String(d)).slice(0, 4));
  };
  const backspace = () => { setError(""); setPin((p) => p.slice(0, -1)); };
  const clear = () => { setError(""); setPin(""); };

  // Auto-submit when 4 digits entered
  useEffect(() => { if (pin.length === 4 && !submitting) submit(); /* eslint-disable-next-line */ }, [pin]);

  return (
    <div className="mm-dark min-h-screen bg-[var(--mm-bg)] grain flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="section-label mb-3 flex items-center justify-center gap-2">
            <ChefHat size={13} /> Back office
          </div>
          <h1 className="font-display italic text-4xl md:text-5xl text-[var(--mm-cream)] leading-tight mb-2">
            Sign in to the till.
          </h1>
          <p className="text-[var(--mm-cream-muted)] font-light text-sm">
            Enter your staff email + 4-digit PIN. Fresh accounts, use the password sign-in first.
          </p>
        </div>

        <div className="border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] p-6" data-testid="staff-pin-gate">
          <label className="block section-label text-[0.6rem] mb-2">Staff email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@mamaminasoulfeed.com"
            data-testid="staff-pin-email"
            className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2.5 text-sm focus:border-[var(--mm-accent)] outline-none mb-5"
          />

          <label className="block section-label text-[0.6rem] mb-3">Enter PIN</label>
          <div className="flex items-center justify-center gap-3 mb-5" data-testid="staff-pin-dots">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                data-testid={`staff-pin-dot-${i}`}
                data-filled={pin.length > i ? "true" : "false"}
                className={`
                  w-4 h-4 rounded-full transition-all duration-150
                  ${pin.length > i ? "bg-[var(--mm-accent)] scale-100" : "border border-[var(--mm-line)] bg-transparent scale-90"}
                `}
              />
            ))}
          </div>

          {/* Keypad */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
              <button
                key={d}
                onClick={() => tap(d)}
                data-testid={`staff-pin-key-${d}`}
                className="h-14 border border-[var(--mm-line)] text-[var(--mm-cream)] text-2xl font-display italic hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] active:bg-[var(--mm-accent)]/10 transition-all"
              >
                {d}
              </button>
            ))}
            <button
              onClick={clear}
              data-testid="staff-pin-clear"
              className="h-14 border border-[var(--mm-line)] text-[var(--mm-cream-muted)] text-[0.62rem] tracking-[0.22em] uppercase hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] transition-all"
            >
              Clear
            </button>
            <button
              onClick={() => tap(0)}
              data-testid="staff-pin-key-0"
              className="h-14 border border-[var(--mm-line)] text-[var(--mm-cream)] text-2xl font-display italic hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] active:bg-[var(--mm-accent)]/10 transition-all"
            >
              0
            </button>
            <button
              onClick={backspace}
              data-testid="staff-pin-backspace"
              className="h-14 border border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] transition-all inline-flex items-center justify-center"
            >
              <Delete size={18} />
            </button>
          </div>

          {error && (
            <div className="text-xs text-red-400 tracking-wide mb-3 text-center" data-testid="staff-pin-error">{error}</div>
          )}

          <button
            onClick={submit}
            disabled={submitting || pin.length !== 4 || !email.trim()}
            data-testid="staff-pin-submit"
            className="w-full btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            {submitting ? "Signing in..." : "Sign in with PIN"}
          </button>
        </div>

        <div className="text-center mt-6">
          <button
            onClick={onPasswordLogin}
            data-testid="staff-signin-btn"
            className="text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors"
          >
            Or sign in with email + password
          </button>
        </div>
      </div>
    </div>
  );
}
